/**
 * Pipeline Engineer API client — /api/v1/pipeline-engineer/*
 *
 * Follows the same CSRF + credentials:include pattern as the MDM client
 * (frontend/src/features/mdm/api/mdm-client.ts).
 *
 * All responses from the platform router are wrapped in APIEnvelope
 * {data: T, meta: {...}}. This client unwraps .data before returning,
 * so callers receive the payload directly.
 *
 * Field names are snake_case from the backend. This client applies
 * snakeToCamel so callers receive camelCase objects matching the
 * PEPipeline, CredentialsResult, TestConnectionResult interfaces.
 */

import type {
  PEPipeline,
  PipelineCreatePayload,
  PipelineUpdatePayload,
  CredentialsPayload,
  CredentialsResult,
  TestConnectionResult,
  PEJob,
  PEJobRun,
  JobRunQueued,
  SQLPreviewResult,
  SchemaDriftApplyResult,
  SchemaDriftCheck,
  SchemaDriftImpact,
  SchemaDriftImpactApplyResult,
  RunAllResult,
  RunMode,
  DeleteAllJobsResult,
  JobCreatePayload,
  JobUpdatePayload,
  SourceTablesPayload,
  ColumnMappingsPayload,
  CTEStepsPayload,
  FiltersPayload,
  DQRulesPayload,
  PETask,
  PEProposal,
  CTESuggestionResult,
  CTERefineResult,
  NLToSQLResult,
  CTEStep,
  SchemaAccessValidationResult,
  MappingProposalResult,
  AutoCreatePipelineResult,
  JobColumnAutoMapResult,
  PipelineScheduleUpdatePayload,
} from "../types";

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

const BASE = "/api/v1/pipeline-engineer";
/** Agentic endpoints — Stories 6.2–6.5 backend, Story 6.6 frontend. */
const BASE_AGENT = "/api/pipeline-engineer-agent";

// Mirrors SAFE_METHODS in ztd_platform/api/core/middleware/csrf.py
const CSRF_REQUIRED_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

function snakeToCamel(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase());
}

function transformKeys(obj: unknown): unknown {
  if (Array.isArray(obj)) {
    return obj.map(transformKeys);
  }
  if (obj !== null && typeof obj === "object") {
    return Object.fromEntries(
      Object.entries(obj as Record<string, unknown>).map(([key, value]) => [
        snakeToCamel(key),
        key === "config" || key.endsWith("_config")
          ? value
          : transformKeys(value),
      ]),
    );
  }
  return obj;
}

/** Read the CSRF double-submit cookie that FastAPI's middleware validates. */
function getCsrfToken(): string {
  return document.cookie.match(/ztd_csrf_token=([^;]+)/)?.[1] ?? "";
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const method = (init?.method ?? "GET").toUpperCase();
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(CSRF_REQUIRED_METHODS.has(method)
        ? { "X-CSRF-Token": getCsrfToken() }
        : {}),
      ...(init?.headers as Record<string, string>),
    },
    credentials: "include",
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: res.statusText }));
    const detail = body.detail;
    let message: string;
    if (typeof detail === "string") {
      message = detail;
    } else if (Array.isArray(detail)) {
      message = detail
        .map((d: { msg?: string }) => d.msg ?? JSON.stringify(d))
        .join("; ");
    } else {
      message = `PE API error: ${res.status} ${res.statusText}`;
    }
    throw new Error(message);
  }

  // DELETE /pipelines/{id} returns 204 No Content.
  if (res.status === 204) return undefined as unknown as T;

  const json = await res.json();
  // Unwrap APIEnvelope {data, meta} — bare responses pass through as-is.
  const payload =
    json && typeof json === "object" && "data" in json ? json.data : json;
  // Apply snake_case → camelCase key transform.
  return transformKeys(payload) as T;
}

// ---------------------------------------------------------------------------
// Pipeline CRUD
// ---------------------------------------------------------------------------

/** GET /api/v1/pipeline-engineer/pipelines */
export async function listPipelines(
  limit = 50,
  offset = 0,
): Promise<PEPipeline[]> {
  return req<PEPipeline[]>(`/pipelines?limit=${limit}&offset=${offset}`);
}

/** POST /api/v1/pipeline-engineer/pipelines — creates a pipeline. */
export async function createPipeline(
  body: PipelineCreatePayload,
): Promise<PEPipeline> {
  return req<PEPipeline>("/pipelines", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** GET /api/v1/pipeline-engineer/pipelines/{id} */
export async function getPipeline(id: string): Promise<PEPipeline> {
  return req<PEPipeline>(`/pipelines/${id}`);
}

/** PUT /api/v1/pipeline-engineer/pipelines/{id} */
export async function updatePipeline(
  id: string,
  body: PipelineUpdatePayload,
): Promise<PEPipeline> {
  return req<PEPipeline>(`/pipelines/${id}`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
}

/**
 * DELETE /api/v1/pipeline-engineer/pipelines/{id}
 * Backend returns 204 No Content — resolves to undefined.
 */
export async function deletePipeline(id: string): Promise<void> {
  await req<undefined>(`/pipelines/${id}`, { method: "DELETE" });
}

// ---------------------------------------------------------------------------
// Credentials and test-connection (Story 1.5 backend)
// ---------------------------------------------------------------------------

/**
 * POST /api/v1/pipeline-engineer/pipelines/{id}/credentials
 *
 * SECURITY: The payload contains plaintext credentials. The caller MUST
 * discard the CredentialsPayload immediately after this call completes.
 * The response returns opaque vault references only — never plaintext.
 */
export async function storeCredentials(
  pipelineId: string,
  body: CredentialsPayload,
): Promise<CredentialsResult> {
  return req<CredentialsResult>(`/pipelines/${pipelineId}/credentials`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/**
 * POST /api/v1/pipeline-engineer/pipelines/{id}/test-connection
 *
 * Pass `side` to test only the source or only the target connector
 * (independently — each hits the backend's own independent try/except,
 * so a source failure never affects a target-only test or vice versa).
 * Omit `side` to test both, as before.
 */
export async function testConnection(
  pipelineId: string,
  side?: "source" | "target",
): Promise<TestConnectionResult> {
  const query = side ? `?side=${side}` : "";
  return req<TestConnectionResult>(
    `/pipelines/${pipelineId}/test-connection${query}`,
    { method: "POST" },
  );
}

/**
 * POST /api/v1/pipeline-engineer/test-connection-adhoc
 *
 * Tests a source/target connection using whatever is currently typed into
 * the Create/Edit Pipeline form, BEFORE the pipeline has been saved for the
 * first time. Nothing is persisted server-side — no pipeline row, no
 * Vault/secret-reference row. Pass plaintext credentials directly; the
 * caller MUST discard them immediately after this call completes (same
 * discipline as `storeCredentials`).
 */
export async function testConnectionAdhoc(body: {
  side: "source" | "target";
  dbType: string;
  config: Record<string, unknown>;
  username?: string;
  password?: string;
  clientId?: string;
  clientSecret?: string;
}): Promise<TestConnectionResult> {
  return req<TestConnectionResult>("/test-connection-adhoc", {
    method: "POST",
    body: JSON.stringify({
      side: body.side,
      db_type: body.dbType,
      config: body.config,
      username: body.username,
      password: body.password,
      client_id: body.clientId,
      client_secret: body.clientSecret,
    }),
  });
}

/**
 * POST /api/v1/pipeline-engineer/pipelines/{id}/connect-existing
 *
 * Reuses one of the project's saved connections (the shared cross-agent
 * library — same rows `apiv2.listSourceConnections`/`listTargetConnections`
 * read) as this pipeline's source or target: config AND credentials, in one
 * call. The resolved plaintext password is handled entirely server-side and
 * never appears in this response — only the updated pipeline (with a fresh
 * opaque `source_secret_ref`/`target_secret_ref`) comes back.
 */
export async function connectExistingConnection(
  pipelineId: string,
  side: "source" | "target",
  connectionId: string,
): Promise<PEPipeline> {
  return req<PEPipeline>(`/pipelines/${pipelineId}/connect-existing`, {
    method: "POST",
    body: JSON.stringify({ side, connection_id: connectionId }),
  });
}

/**
 * GET /api/v1/pipeline-engineer/pipelines/{id}/source/schemas
 *
 * Returns the non-system schemas visible to the pipeline's source
 * connection — feeds the Source tab's schema picker so a user can choose
 * which schema a job's source table(s) live in.
 */
export async function listSourceSchemas(pipelineId: string): Promise<string[]> {
  const result = await req<{ schemas: string[] }>(
    `/pipelines/${pipelineId}/source/schemas`,
  );
  return result.schemas;
}

/**
 * GET /api/v1/pipeline-engineer/pipelines/{id}/target/schemas
 *
 * Returns the non-system schemas visible to the pipeline's target
 * connection — feeds the Target tab's schema picker so a user can choose
 * which schema a job's target table lives in (mapping an existing table in
 * it, or creating a new one there).
 */
export async function listTargetSchemas(pipelineId: string): Promise<string[]> {
  const result = await req<{ schemas: string[] }>(
    `/pipelines/${pipelineId}/target/schemas`,
  );
  return result.schemas;
}

// ---------------------------------------------------------------------------
// Job CRUD
// ---------------------------------------------------------------------------

/** GET /api/v1/pipeline-engineer/pipelines/{pid}/jobs */
export async function listJobs(pipelineId: string): Promise<PEJob[]> {
  const pageSize = 200;
  const jobs: PEJob[] = [];
  let offset = 0;
  let page: PEJob[];
  do {
    page = await req<PEJob[]>(
      `/pipelines/${pipelineId}/jobs?limit=${pageSize}&offset=${offset}`,
    );
    jobs.push(...page);
    offset += page.length;
  } while (page.length === pageSize);
  return jobs;
}

/** POST /api/v1/pipeline-engineer/pipelines/{pid}/jobs */
export async function createJob(
  pipelineId: string,
  body: JobCreatePayload,
): Promise<PEJob> {
  return req<PEJob>(`/pipelines/${pipelineId}/jobs`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** GET /api/v1/pipeline-engineer/pipelines/{pid}/jobs/{jid} */
export async function getJob(
  pipelineId: string,
  jobId: string,
): Promise<PEJob> {
  return req<PEJob>(`/pipelines/${pipelineId}/jobs/${jobId}`);
}

/**
 * PUT /api/v1/pipeline-engineer/pipelines/{pid}/jobs/{jid}
 *
 * Updates core job fields only (load_strategy, primary_keys, scd2_config,
 * etc.) — NOT the JSONB sections (source_tables, column_mappings, cte_steps,
 * filters, dq_rules), which have their own dedicated PATCH endpoints.
 * The backend router only registers this route as PUT
 * (see ztd_app/api/modules/pipeline_engineer/router.py::update_job) — using
 * PATCH here returned 405 Method Not Allowed for every Strategy/SCD2 save.
 */
export async function updateJob(
  pipelineId: string,
  jobId: string,
  body: JobUpdatePayload,
): Promise<PEJob> {
  return req<PEJob>(`/pipelines/${pipelineId}/jobs/${jobId}`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
}

/**
 * DELETE /api/v1/pipeline-engineer/pipelines/{pid}/jobs/{jid}
 * Soft-delete: sets is_active=False.
 */
export async function deleteJob(
  pipelineId: string,
  jobId: string,
): Promise<void> {
  await req<undefined>(`/pipelines/${pipelineId}/jobs/${jobId}`, {
    method: "DELETE",
  });
}

/**
 * DELETE /api/v1/pipeline-engineer/pipelines/{pid}/jobs
 * Soft-deletes every active job in the pipeline (bulk companion to deleteJob).
 */
export async function deleteAllJobs(
  pipelineId: string,
): Promise<DeleteAllJobsResult> {
  return req<DeleteAllJobsResult>(`/pipelines/${pipelineId}/jobs`, {
    method: "DELETE",
  });
}

// ---------------------------------------------------------------------------
// Job section PATCH endpoints
// ---------------------------------------------------------------------------

/** PATCH .../source-tables */
export async function patchSourceTables(
  pipelineId: string,
  jobId: string,
  body: SourceTablesPayload,
): Promise<PEJob> {
  return req<PEJob>(`/pipelines/${pipelineId}/jobs/${jobId}/source-tables`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

/** PATCH .../column-mappings */
export async function patchColumnMappings(
  pipelineId: string,
  jobId: string,
  body: ColumnMappingsPayload,
): Promise<PEJob> {
  return req<PEJob>(`/pipelines/${pipelineId}/jobs/${jobId}/column-mappings`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

/** PATCH .../cte-steps */
export async function patchCTESteps(
  pipelineId: string,
  jobId: string,
  body: CTEStepsPayload,
): Promise<PEJob> {
  return req<PEJob>(`/pipelines/${pipelineId}/jobs/${jobId}/cte-steps`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

/** PATCH .../filters */
export async function patchFilters(
  pipelineId: string,
  jobId: string,
  body: FiltersPayload,
): Promise<PEJob> {
  return req<PEJob>(`/pipelines/${pipelineId}/jobs/${jobId}/filters`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

/** PATCH .../dq-rules */
export async function patchDQRules(
  pipelineId: string,
  jobId: string,
  body: DQRulesPayload,
): Promise<PEJob> {
  return req<PEJob>(`/pipelines/${pipelineId}/jobs/${jobId}/dq-rules`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

// ---------------------------------------------------------------------------
// Job execution — run, run-all, run history
// ---------------------------------------------------------------------------

/**
 * POST /pipelines/{pid}/jobs/{jid}/run
 * Enqueues an async job run. Returns HTTP 202 immediately.
 */
export async function triggerJobRun(
  pipelineId: string,
  jobId: string,
): Promise<JobRunQueued> {
  return req<JobRunQueued>(`/pipelines/${pipelineId}/jobs/${jobId}/run`, {
    method: "POST",
  });
}

/**
 * GET /pipelines/{pid}/runs
 * Returns paginated run history across all jobs in a pipeline, most-recent
 * first. Used by JobList to derive each job's latest run status for its
 * progress bar with a single request instead of one per job.
 */
export async function listPipelineRuns(
  pipelineId: string,
  limit = 200,
): Promise<PEJobRun[]> {
  const runs = await req<PEJobRun[]>(
    `/pipelines/${pipelineId}/runs?limit=${limit}`,
  );
  // Backend returns dq_results: null for queued/running runs — normalise at the
  // API boundary so callers always receive a non-null DQRuleResult[].
  return runs.map((r) => ({ ...r, dqResults: r.dqResults ?? [] }));
}

/**
 * GET /pipelines/{pid}/jobs/{jid}/runs
 * Returns paginated list of runs for a job (most-recent first).
 */
export async function listJobRuns(
  pipelineId: string,
  jobId: string,
  limit = 20,
): Promise<PEJobRun[]> {
  const runs = await req<PEJobRun[]>(
    `/pipelines/${pipelineId}/jobs/${jobId}/runs?limit=${limit}`,
  );
  // Backend returns dq_results: null for queued/running runs — normalise at the
  // API boundary so callers always receive a non-null DQRuleResult[].
  return runs.map((r) => ({ ...r, dqResults: r.dqResults ?? [] }));
}

/**
 * GET /pipelines/{pid}/jobs/{jid}/runs/{run_id}
 * Returns a single PEJobRun record for polling.
 */
export async function getJobRun(
  pipelineId: string,
  jobId: string,
  runId: string,
): Promise<PEJobRun> {
  const run = await req<PEJobRun>(
    `/pipelines/${pipelineId}/jobs/${jobId}/runs/${runId}`,
  );
  // Backend returns dq_results: null for queued/running runs — normalise at the
  // API boundary so callers always receive a non-null DQRuleResult[].
  return { ...run, dqResults: run.dqResults ?? [] };
}

/**
 * POST /pipelines/{pid}/jobs/{jid}/runs/{run_id}/stop
 * Stops a queued or running run. A run that stopped reporting progress comes
 * back failed at once; a live one comes back with stopRequestedAt set and ends
 * "cancelled" before its next write to the target. 409 if it already finished.
 */
export async function stopJobRun(
  pipelineId: string,
  jobId: string,
  runId: string,
): Promise<PEJobRun> {
  const run = await req<PEJobRun>(
    `/pipelines/${pipelineId}/jobs/${jobId}/runs/${runId}/stop`,
    { method: "POST" },
  );
  return { ...run, dqResults: run.dqResults ?? [] };
}

/**
 * POST /pipelines/{pid}/run-all
 * Enqueues all active jobs in the pipeline.
 *
 * @param runMode "parallel" (default, all at once), "sequential" (one at a
 *   time, creation order), or "custom" (one at a time, by execution_order).
 */
export async function runAllJobs(
  pipelineId: string,
  runMode: RunMode = "parallel",
): Promise<RunAllResult> {
  return req<RunAllResult>(
    `/pipelines/${pipelineId}/run-all?run_mode=${runMode}`,
    { method: "POST" },
  );
}

/**
 * PUT /pipelines/{id}/schedule
 * Configures (or disables) a cron schedule that automatically triggers
 * Run All Jobs for this pipeline. Returns the updated pipeline, including
 * the newly computed `scheduleNextRunAt`.
 */
export async function updatePipelineSchedule(
  pipelineId: string,
  body: PipelineScheduleUpdatePayload,
): Promise<PEPipeline> {
  return req<PEPipeline>(`/pipelines/${pipelineId}/schedule`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
}

// ---------------------------------------------------------------------------
// SQL Preview
// ---------------------------------------------------------------------------

/**
 * POST /pipelines/{pid}/jobs/{jid}/preview-sql
 * Returns generated SQL without executing any data load.
 */
export async function previewSQL(
  pipelineId: string,
  jobId: string,
): Promise<SQLPreviewResult> {
  return req<SQLPreviewResult>(
    `/pipelines/${pipelineId}/jobs/${jobId}/preview-sql`,
    { method: "POST" },
  );
}

/**
 * POST /pipelines/{pid}/jobs/{jid}/schema-drift
 * Compares the job's live source and target with its mapping. When there is
 * drift the result carries a pending proposalId to approve or reject.
 */
export async function checkSchemaDrift(
  pipelineId: string,
  jobId: string,
): Promise<SchemaDriftCheck> {
  return req<SchemaDriftCheck>(
    `/pipelines/${pipelineId}/jobs/${jobId}/schema-drift`,
    { method: "POST" },
  );
}

/**
 * POST /api/v1/pipeline-engineer/pipelines/{pid}/jobs/{jid}/schema-drift/impact
 * Every job in the project that reads this job's source (and everything
 * downstream of those), each checked for drift; pending proposals for review.
 */
export async function checkSchemaDriftImpact(
  pipelineId: string,
  jobId: string,
): Promise<SchemaDriftImpact> {
  return req<SchemaDriftImpact>(
    `/pipelines/${pipelineId}/jobs/${jobId}/schema-drift/impact`,
    { method: "POST" },
  );
}

/**
 * POST /api/v1/pipeline-engineer/schema-drift/impact/apply
 * "Review and fix all": apply the approved proposals upstream first (each its
 * own transaction and approval audit event), then rerun the jobs in order.
 */
export async function applySchemaDriftImpact(
  proposalIds: string[],
  rerun = true,
): Promise<SchemaDriftImpactApplyResult> {
  return req<SchemaDriftImpactApplyResult>(`/schema-drift/impact/apply`, {
    method: "POST",
    body: JSON.stringify({ proposal_ids: proposalIds, rerun }),
  });
}

// ---------------------------------------------------------------------------
// Agentic API client helper — Story 6.6
// ---------------------------------------------------------------------------

/**
 * Agentic request helper — identical to req() but uses BASE_AGENT prefix.
 * All agentic responses are snake_case from the backend and are converted to
 * camelCase by transformKeys before returning to callers.
 */
async function agentReq<T>(path: string, init?: RequestInit): Promise<T> {
  const method = (init?.method ?? "GET").toUpperCase();
  const res = await fetch(`${BASE_AGENT}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(CSRF_REQUIRED_METHODS.has(method)
        ? { "X-CSRF-Token": getCsrfToken() }
        : {}),
      ...(init?.headers as Record<string, string>),
    },
    credentials: "include",
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: res.statusText }));
    const detail = body.detail;
    let message: string;
    if (typeof detail === "string") {
      message = detail;
    } else if (Array.isArray(detail)) {
      message = detail
        .map((d: { msg?: string }) => d.msg ?? JSON.stringify(d))
        .join("; ");
    } else {
      message = `PE Agentic API error: ${res.status} ${res.statusText}`;
    }
    throw new Error(message);
  }

  const json = await res.json();
  const payload =
    json && typeof json === "object" && "data" in json ? json.data : json;
  return transformKeys(payload) as T;
}

// ---------------------------------------------------------------------------
// Schema Introspection (Story 6.2 backend)
// ---------------------------------------------------------------------------

/**
 * POST /api/pipeline-engineer-agent/introspect
 * Starts async schema introspection for either side. Returns HTTP 202 with
 * task_id. Pass side="target" to introspect the target instead of the
 * default source (needed by the Auto-Map mapping mode, which requires both).
 */
export async function triggerIntrospection(
  pipelineId: string,
  projectId: string,
  tableNames?: string[] | null,
  side: "source" | "target" = "source",
  schemaName?: string | null,
): Promise<{ taskId: string; status: string }> {
  return agentReq<{ taskId: string; status: string }>("/introspect", {
    method: "POST",
    body: JSON.stringify({
      pipeline_id: pipelineId,
      project_id: projectId,
      table_names: tableNames ?? null,
      side,
      schema_name: schemaName ?? null,
    }),
  });
}

/**
 * GET /api/pipeline-engineer-agent/tasks/{task_id}
 * Polls a PETask for status and result.
 */
export async function pollTask(taskId: string): Promise<PETask> {
  return agentReq<PETask>(`/tasks/${taskId}`);
}

// ---------------------------------------------------------------------------
// Job Proposals (Story 6.3 backend)
// ---------------------------------------------------------------------------

/**
 * POST /api/pipeline-engineer-agent/propose-jobs
 * Starts async LLM job proposal generation. Returns HTTP 202 with proposal_id.
 */
export async function proposeJob(
  pipelineId: string,
  taskId: string,
  userPrompt: string,
  projectId: string,
  classification = 2,
): Promise<{ proposalId: string; status: string }> {
  return agentReq<{ proposalId: string; status: string }>("/propose-jobs", {
    method: "POST",
    body: JSON.stringify({
      pipeline_id: pipelineId,
      task_id: taskId,
      user_prompt: userPrompt,
      project_id: projectId,
      classification,
    }),
  });
}

/**
 * GET /api/pipeline-engineer-agent/proposals/{proposal_id}
 * Polls a PEProposal for task completion and user decision status.
 * Poll until taskStatus="completed" before displaying items.
 */
export async function pollProposal(proposalId: string): Promise<PEProposal> {
  return agentReq<PEProposal>(`/proposals/${proposalId}`);
}

/**
 * POST /api/pipeline-engineer-agent/proposals/{proposal_id}/accept
 * Accepts the proposal and bulk-creates PEJob rows in the pipeline.
 */
export async function acceptProposal(proposalId: string): Promise<{
  proposalId: string;
  status: string;
  jobsCreated: number;
  jobsSkipped: number;
}> {
  return agentReq<{
    proposalId: string;
    status: string;
    jobsCreated: number;
    jobsSkipped: number;
  }>(`/proposals/${proposalId}/accept`, { method: "POST" });
}

/**
 * POST /api/pipeline-engineer-agent/proposals/{proposal_id}/accept-item
 * Accepts exactly ONE proposed job (identified by jobName) from the
 * proposal, leaving the rest pending — powers the per-row Accept button.
 */
export async function acceptProposalItem(
  proposalId: string,
  jobName: string,
): Promise<{
  proposalId: string;
  status: string;
  jobId: string;
  jobName: string;
  itemsRemaining: number;
}> {
  return agentReq<{
    proposalId: string;
    status: string;
    jobId: string;
    jobName: string;
    itemsRemaining: number;
  }>(`/proposals/${proposalId}/accept-item`, {
    method: "POST",
    body: JSON.stringify({ job_name: jobName }),
  });
}

/**
 * POST /api/pipeline-engineer-agent/proposals/{proposal_id}/reject
 * Rejects the proposal — no jobs are created.
 */
export async function rejectProposal(
  proposalId: string,
): Promise<{ proposalId: string; status: string }> {
  return agentReq<{ proposalId: string; status: string }>(
    `/proposals/${proposalId}/reject`,
    { method: "POST" },
  );
}

/**
 * POST /api/pipeline-engineer-agent/proposals/{proposal_id}/apply-drift
 * Approves a schema-drift proposal: runs its DDL on the target (one
 * transaction), updates the job's mapping and, by default, reruns the job.
 * Rejecting uses rejectProposal().
 */
export async function applySchemaDrift(
  proposalId: string,
  rerun = true,
): Promise<SchemaDriftApplyResult> {
  return agentReq<SchemaDriftApplyResult>(
    `/proposals/${proposalId}/apply-drift`,
    {
      method: "POST",
      body: JSON.stringify({ rerun }),
    },
  );
}

/**
 * POST /api/pipeline-engineer-agent/proposals/{proposal_id}/auto-create-pipeline
 * One-click pathway from a proposal (typically produced by auto-map) straight
 * to a running pipeline: auto-creates any missing target tables, bulk-creates
 * jobs, and (unless runAfterCreate=false) immediately runs each created job.
 */
export async function autoCreatePipeline(
  proposalId: string,
  runAfterCreate = true,
): Promise<AutoCreatePipelineResult> {
  return agentReq<AutoCreatePipelineResult>(
    `/proposals/${proposalId}/auto-create-pipeline?run_after_create=${runAfterCreate}`,
    { method: "POST" },
  );
}

// ---------------------------------------------------------------------------
// CTE Generation and Refinement (Story 6.4 backend)
// ---------------------------------------------------------------------------

/**
 * POST /api/pipeline-engineer-agent/jobs/{job_id}/generate-cte
 * Synchronous LLM call — returns suggested CTE steps for the job.
 * Steps are NOT applied automatically; caller decides whether to PATCH.
 */
export async function suggestCTESteps(
  jobId: string,
  projectId: string,
  hints?: string | null,
  classification = 2,
): Promise<CTESuggestionResult> {
  return agentReq<CTESuggestionResult>(`/jobs/${jobId}/generate-cte`, {
    method: "POST",
    body: JSON.stringify({
      project_id: projectId,
      hints: hints ?? null,
      classification,
    }),
  });
}

/**
 * POST /api/pipeline-engineer-agent/jobs/{job_id}/refine-cte
 * Synchronous LLM call — refines a single CTE step via natural language.
 * Only the target step's SQL is updated; siblings are preserved.
 */
export async function refineCTEStep(
  jobId: string,
  projectId: string,
  stepName: string,
  currentSql: string,
  instruction: string,
  currentCteSteps: CTEStep[],
  classification = 2,
): Promise<CTERefineResult> {
  return agentReq<CTERefineResult>(`/jobs/${jobId}/refine-cte`, {
    method: "POST",
    body: JSON.stringify({
      project_id: projectId,
      step_name: stepName,
      current_sql: currentSql,
      instruction,
      current_cte_steps: currentCteSteps.map((s) => ({
        step_name: s.stepName,
        sql_body: s.sqlBody,
        description: s.description,
      })),
      classification,
    }),
  });
}

// ---------------------------------------------------------------------------
// NL→SQL Translation (Story 6.5 backend)
// ---------------------------------------------------------------------------

/**
 * POST /api/pipeline-engineer-agent/jobs/{job_id}/nl-to-sql
 * Synchronous LLM call — translates a NL description to a SQL expression.
 * Returns {expression, error} — expression is null when error is set.
 */
export async function nlToSQL(
  jobId: string,
  projectId: string,
  targetCol: string,
  nlDescription: string,
  classification = 2,
): Promise<NLToSQLResult> {
  return agentReq<NLToSQLResult>(`/jobs/${jobId}/nl-to-sql`, {
    method: "POST",
    body: JSON.stringify({
      project_id: projectId,
      target_col: targetCol,
      nl_description: nlDescription,
      classification,
    }),
  });
}

/**
 * POST /api/pipeline-engineer-agent/jobs/{job_id}/auto-map-columns
 * Deterministic (no LLM) column-mapping proposal for ONE existing job
 * against an explicitly selected existing target table. Requires a
 * completed source AND target introspection task (see triggerIntrospection's
 * `side` param) — the job's driver source table is resolved server-side.
 * The result is NOT persisted; save it via patchColumnMappings() once reviewed.
 */
export async function autoMapJobColumns(
  jobId: string,
  sourceTaskId: string,
  targetTaskId: string,
  targetTableName: string,
): Promise<JobColumnAutoMapResult> {
  return agentReq<JobColumnAutoMapResult>(`/jobs/${jobId}/auto-map-columns`, {
    method: "POST",
    body: JSON.stringify({
      source_task_id: sourceTaskId,
      target_task_id: targetTaskId,
      target_table_name: targetTableName,
    }),
  });
}

/**
 * POST /api/pipeline-engineer-agent/jobs/{job_id}/auto-map-columns-from-sql
 * Companion to autoMapJobColumns() for jobs using a custom SQL pushdown
 * override: instead of fuzzy-matching a driver source *table* (which the
 * override SQL replaces entirely), this parses the override SQL's own
 * SELECT-list column aliases and matches those onto the target table's
 * real columns. No source introspection task is needed — only the target
 * side must already be introspected.
 */
export async function autoMapJobColumnsFromSql(
  jobId: string,
  targetTaskId: string,
  targetTableName: string,
  sourceSql: string,
): Promise<JobColumnAutoMapResult> {
  return agentReq<JobColumnAutoMapResult>(
    `/jobs/${jobId}/auto-map-columns-from-sql`,
    {
      method: "POST",
      body: JSON.stringify({
        target_task_id: targetTaskId,
        target_table_name: targetTableName,
        source_sql: sourceSql,
      }),
    },
  );
}

// ---------------------------------------------------------------------------
// Schema access validation + alternate mapping pathways (Story 6.7)
// ---------------------------------------------------------------------------

/**
 * POST /api/pipeline-engineer-agent/validate-schema-access
 * Confirms the pipeline's saved credentials can actually READ table/column
 * metadata for the source and target — not just that a connection succeeds.
 * Each side is evaluated independently and never raises; zero reachable
 * tables is reported as a warning rather than a hard failure.
 */
export async function validateSchemaAccess(
  pipelineId: string,
): Promise<SchemaAccessValidationResult> {
  return agentReq<SchemaAccessValidationResult>(
    `/validate-schema-access?pipeline_id=${pipelineId}`,
    { method: "POST" },
  );
}

/**
 * POST /api/pipeline-engineer-agent/auto-map
 * Deterministic fuzzy-match auto-mapping — requires a completed source AND
 * target introspection task (see triggerIntrospection's `side` param).
 * No LLM call; the proposal is returned finished, synchronously.
 */
export async function autoMap(
  pipelineId: string,
  sourceTaskId: string,
  targetTaskId: string,
): Promise<MappingProposalResult> {
  return agentReq<MappingProposalResult>("/auto-map", {
    method: "POST",
    body: JSON.stringify({
      pipeline_id: pipelineId,
      source_task_id: sourceTaskId,
      target_task_id: targetTaskId,
    }),
  });
}

/**
 * POST /api/pipeline-engineer-agent/import-mapping
 * Uploads a manually-prepared CSV/XLSX mapping sheet. Multipart form data —
 * bypasses agentReq()'s JSON Content-Type, so the fetch + error handling is
 * duplicated here rather than reusing agentReq directly.
 */
export async function importMapping(
  pipelineId: string,
  file: File,
): Promise<MappingProposalResult> {
  const formData = new FormData();
  formData.append("file", file);
  const res = await fetch(
    `${BASE_AGENT}/import-mapping?pipeline_id=${pipelineId}`,
    {
      method: "POST",
      body: formData,
      headers: { "X-CSRF-Token": getCsrfToken() },
      credentials: "include",
    },
  );

  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: res.statusText }));
    const detail = body.detail;
    let message: string;
    if (typeof detail === "string") {
      message = detail;
    } else if (Array.isArray(detail)) {
      message = detail
        .map((d: { msg?: string }) => d.msg ?? JSON.stringify(d))
        .join("; ");
    } else {
      message = `PE Agentic API error: ${res.status} ${res.statusText}`;
    }
    throw new Error(message);
  }

  const json = await res.json();
  const payload =
    json && typeof json === "object" && "data" in json ? json.data : json;
  return transformKeys(payload) as MappingProposalResult;
}

/**
 * GET /api/pipeline-engineer-agent/data-converter-projects/{projectId}/mapping-versions
 * Lists every saved STTM mapping version for a Data Converter project
 * (most-recent first), so the "From Data Converter" picker can offer an
 * older saved version instead of only ever the current one.
 */
export interface DataConverterMappingVersion {
  historyId: string;
  savedAt: string;
  tableCount: number;
}

export async function listDataConverterMappingVersions(
  projectId: string,
): Promise<DataConverterMappingVersion[]> {
  const result = await agentReq<{ versions: DataConverterMappingVersion[] }>(
    `/data-converter-projects/${projectId}/mapping-versions`,
  );
  return result.versions;
}

/**
 * POST /api/pipeline-engineer-agent/from-data-converter
 * Reuses a Data Converter project's already-finalized source-to-target
 * mapping as a Pipeline Engineer proposal. Pass historyId to reuse an
 * older saved version (see listDataConverterMappingVersions) instead of
 * the project's current mapping.
 */
export async function mappingFromDataConverter(
  pipelineId: string,
  projectId: string,
  historyId?: string | null,
): Promise<MappingProposalResult> {
  return agentReq<MappingProposalResult>("/from-data-converter", {
    method: "POST",
    body: JSON.stringify({
      pipeline_id: pipelineId,
      project_id: projectId,
      history_id: historyId ?? null,
    }),
  });
}
